import React from 'react';
import { DataFrame } from './dataUtils';

// Toast component for copy feedback
const CopyToast: React.FC<{ message: string }> = ({ message }) => (
  <div
    style={{
      position: 'fixed',
      bottom: 32,
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#2a2a4a',
      color: '#fff',
      padding: '10px 22px',
      borderRadius: 8,
      fontSize: 15,
      fontWeight: 600,
      boxShadow: '0 2px 12px rgba(0,0,0,0.13)',
      zIndex: 9999,
      opacity: 0.97,
      pointerEvents: 'none',
      letterSpacing: 0.2,
    }}
    role="status"
    aria-live="polite"
  >
    {message}
  </div>
);

interface TopGenesTableProps {
  topGenes: Record<string, string[]>;
  df: DataFrame;
}

const TopGenesTable: React.FC<TopGenesTableProps> = ({ topGenes, df }) => {
  // All groups
  const groups = Object.keys(topGenes);
  // Union of all top genes (preserve order: by group, then unique)
  const allTopGenes: string[] = [];
  const seen = new Set<string>();
  groups.forEach(group => {
    topGenes[group].forEach(gene => {
      if (!seen.has(gene)) {
        allTopGenes.push(gene);
        seen.add(gene);
      }
    });
  });
  // Count in how many groups each gene appears in top 50
  const geneGroupCount: Record<string, number> = {};
  groups.forEach(group => {
    topGenes[group].forEach(gene => {
      geneGroupCount[gene] = (geneGroupCount[gene] || 0) + 1;
    });
  });
  // Sort genes by number of groups (descending), then alphabetically
  allTopGenes.sort((a, b) => {
    const diff = (geneGroupCount[b] || 0) - (geneGroupCount[a] || 0);
    if (diff !== 0) return diff;
    return a.localeCompare(b);
  });
  // For each gene, get value and rank per group
  // Precompute value/rank maps for efficiency
  const valueMap: Record<string, Record<string, number | null>> = {};
  const rankMap: Record<string, Record<string, number | null>> = {};
  groups.forEach(group => {
    const genes = topGenes[group];
    genes.forEach((gene, idx) => {
      if (!rankMap[gene]) rankMap[gene] = {};
      rankMap[gene][group] = idx + 1;
    });
  });
  allTopGenes.forEach(gene => {
    valueMap[gene] = {};
    groups.forEach(group => {
      valueMap[gene][group] = (df.data[gene] && df.data[gene][group] != null) ? df.data[gene][group] : null;
    });
  });
  const [visibleStart, setVisibleStart] = React.useState(0);
  const visibleCount = 20;
  const totalGenes = allTopGenes.length;
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const rowHeight = 50; // px, matches maxHeight logic
    const scrollTop = e.currentTarget.scrollTop;
    const newStart = Math.floor(scrollTop / rowHeight);
    if (newStart !== visibleStart) setVisibleStart(newStart);
  };
  // Sorting state
  const [sort, setSort] = React.useState<{ col: string | null; type: 'value' | 'rank' | null; dir: 'asc' | 'desc' }>({ col: '__overlap__', type: null, dir: 'desc' });

  // Compute sorted genes
  let sortedGenes = [...allTopGenes];
  if (sort.col === '__overlap__') {
    sortedGenes.sort((a, b) => {
      const aVal = geneGroupCount[a] || 0;
      const bVal = geneGroupCount[b] || 0;
      if (aVal === bVal) return a.localeCompare(b);
      return sort.dir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  } else if (sort.col && sort.type && sort.col !== null) {
    sortedGenes.sort((a, b) => {
      const aVal = sort.type === 'value' ? valueMap[a][sort.col as string] : rankMap[a][sort.col as string];
      const bVal = sort.type === 'value' ? valueMap[b][sort.col as string] : rankMap[b][sort.col as string];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (sort.dir === 'asc') return aVal - bVal;
      return bVal - aVal;
    });
  }
  const [toast, setToast] = React.useState<string | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1200);
  };
  return (
    <>
      <h2 style={{ margin: '0 0 18px 0', color: '#2a2a4a', fontSize: 22, fontWeight: 700, letterSpacing: 0.2, textAlign: 'center' }}>Top Genes Analysis</h2>
      <div
        style={{ maxHeight: 50 * visibleCount + 2, overflowY: 'auto', overflowX: 'auto', borderRadius: 8, boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}
      >
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'min(100%, 1400px)', minWidth: 800, background: '#f9f9f9', borderRadius: 8, tableLayout: 'auto', fontSize: 13, lineHeight: 1.2 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: '#f9f9f9' }}>
            <tr>
              <th rowSpan={2} style={{ position: 'sticky', left: 0, zIndex: 3, border: '1.5px solid #b0b6c3', borderRight: '4px solid #2a2a4a', borderBottom: '4px solid #2a2a4a', padding: 6, background: '#e0e0f8', fontWeight: 700, fontSize: 14, letterSpacing: 0.3, verticalAlign: 'middle', color: '#2a2a4a', minWidth: 60, maxWidth: 90, width: 80, textAlign: 'left' }}>Gene</th>
              <th
                rowSpan={2}
                style={{ border: '1.5px solid #b0b6c3', borderBottom: '4px solid #2a2a4a', padding: 6, background: '#e8f4ff', fontWeight: 700, fontSize: 14, letterSpacing: 0.3, verticalAlign: 'middle', color: '#2a2a4a', minWidth: 36, maxWidth: 48, width: 44, textAlign: 'center', position: 'relative' }}
              >
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <span title="Number of groups in which the gene appears" style={{ fontSize: 18, fontWeight: 700, fontFamily: 'serif', verticalAlign: 'middle' }}>&#8721;</span>
                  <div
                    style={{
                      display: 'inline-block',
                      marginLeft: 6,
                      cursor: 'pointer',
                      position: 'relative',
                    }}
                  >
                    <span
                      style={{
                        borderBottom: '1px dotted #888',
                        color: '#888',
                        fontSize: 15,
                        marginLeft: 2,
                        cursor: 'help',
                      }}
                      tabIndex={0}
                      onMouseEnter={e => {
                        const tooltip = document.getElementById('overlap-tooltip');
                        if (tooltip) tooltip.style.display = 'block';
                      }}
                      onMouseLeave={e => {
                        const tooltip = document.getElementById('overlap-tooltip');
                        if (tooltip) tooltip.style.display = 'none';
                      }}
                      aria-label="Show explanation for overlap count"
                    >
                      ?
                    </span>
                    <div
                      id="overlap-tooltip"
                      style={{
                        display: 'none',
                        position: 'absolute',
                        left: '50%',
                        top: 24,
                        transform: 'translateX(-50%)',
                        background: '#fff',
                        color: '#222',
                        border: '1.5px solid #b0b6c3',
                        borderRadius: 7,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.13)',
                        padding: '10px 16px',
                        fontSize: 14,
                        zIndex: 10,
                        minWidth: 220,
                        maxWidth: 320,
                        whiteSpace: 'normal',
                        pointerEvents: 'none',
                      }}
                    >
                      The overlap count is the number of sample groups in which each gene appears in the top 50 genes for that group.
                    </div>
                  </div>
                  <br />
                  <span style={{ color: sort.col === '__overlap__' ? '#d90429' : '#888', fontSize: 13, display: 'inline-block', marginTop: 2 }}>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 13 }}
                      onClick={e => {
                        e.stopPropagation();
                        setSort(s => ({ col: '__overlap__', type: null, dir: s.col === '__overlap__' && s.dir === 'desc' ? 'asc' : 'desc' }));
                      }}
                      title="Sort by overlap count (number of groups)"
                    >
                      {sort.col === '__overlap__' ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
                    </button>
                  </span>
                </div>
              </th>
              {groups.map(group => (
                <th key={group + '-val'} colSpan={2} style={{ border: '1.5px solid #b0b6c3', padding: 6, background: '#e8f4ff', color: '#2a2a4a', fontWeight: 600, fontSize: 13, textAlign: 'center' }}>{group}</th>
              ))}
            </tr>
            <tr>
              {/* No <th> for gene/overlap columns here since they are rowSpan=2 */}
              {groups.map(group => [
                <th key={group + '-value'} style={{ border: '1.5px solid #b0b6c3', borderBottom: '4px solid #2a2a4a', padding: '1px 1px', fontWeight: 600, background: '#f7fbff', color: '#2a2a4a', fontSize: 12, textAlign: 'center', minWidth: 28, maxWidth: 32, width: 30 }}>
                  Value
                  <br />
                  <button
                    style={{ marginTop: 1, background: 'none', border: 'none', cursor: 'pointer', color: sort.col === group && sort.type === 'value' ? '#d90429' : '#888', fontSize: 11 }}
                    title={`Sort by value (${sort.col === group && sort.type === 'value' && sort.dir === 'asc' ? 'desc' : 'asc'})`}
                    onClick={e => {
                      e.stopPropagation();
                      setSort(s => ({
                        col: group,
                        type: 'value',
                        dir: s.col === group && s.type === 'value' && s.dir === 'asc' ? 'desc' : 'asc',
                      }));
                    }}
                  >
                    {sort.col === group && sort.type === 'value' ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
                  </button>
                </th>,
                <th key={group + '-rank'} style={{ border: '1.5px solid #b0b6c3', borderBottom: '4px solid #2a2a4a', padding: '1px 1px', fontWeight: 600, background: '#e3e6f3', color: '#2a2a4a', fontSize: 12, textAlign: 'center', minWidth: 28, maxWidth: 32, width: 30 }}>
                  Rank
                  <br />
                  <button
                    style={{ marginTop: 1, background: 'none', border: 'none', cursor: 'pointer', color: sort.col === group && sort.type === 'rank' ? '#d90429' : '#888', fontSize: 11 }}
                    title={`Sort by rank (${sort.col === group && sort.type === 'rank' && sort.dir === 'asc' ? 'desc' : 'asc'})`}
                    onClick={e => {
                      e.stopPropagation();
                      setSort(s => ({
                        col: group,
                        type: 'rank',
                        dir: s.col === group && s.type === 'rank' && s.dir === 'asc' ? 'desc' : 'asc',
                      }));
                    }}
                  >
                    {sort.col === group && sort.type === 'rank' ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
                  </button>
                </th>
              ])}
            </tr>
          </thead>
          <tbody>
            {sortedGenes.map(gene => (
              <tr key={gene} style={{ background: '#fff', transition: 'background 0.2s', height: 32 }}>
                <td
                  style={{ border: '1.5px solid #b0b6c3', borderRight: '4px solid #2a2a4a', padding: 5, fontWeight: 700, background: '#f3f1ff', fontSize: 13, letterSpacing: 0.1, color: '#2a2a4a', minWidth: 60, maxWidth: 90, width: 80, cursor: 'pointer', userSelect: 'text', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={gene}
                  onClick={() => {
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(gene);
                      showToast(`Copied: ${gene}`);
                    } else {
                      // fallback for older browsers
                      const textarea = document.createElement('textarea');
                      textarea.value = gene;
                      document.body.appendChild(textarea);
                      textarea.select();
                      try { document.execCommand('copy'); showToast(`Copied: ${gene}`); } catch {}
                      document.body.removeChild(textarea);
                    }
                  }}
                >
                  {gene}
                </td>
                <td style={{ border: '1.5px solid #b0b6c3', padding: 2, fontWeight: 700, background: '#e3e6f3', fontSize: 13, color: '#2a2a4a', minWidth: 36, maxWidth: 48, width: 44, textAlign: 'center' }}>{geneGroupCount[gene]}</td>
                {groups.map(group => [
                  <td key={gene + group + '-value'} style={{ border: '1.5px solid #b0b6c3', padding: '1px 1px', background: '#f7fbff', color: '#2a2a4a', fontSize: 12, textAlign: 'center', fontVariantNumeric: 'tabular-nums', minWidth: 28, maxWidth: 32, width: 30 }}>
                    {valueMap[gene][group] !== null && valueMap[gene][group] !== undefined ? valueMap[gene][group]!.toFixed(3) : <span style={{ color: '#bbb' }}>N/A</span>}
                  </td>,
                  <td key={gene + group + '-rank'} style={{ border: '1.5px solid #b0b6c3', padding: '1px 1px', background: '#e3e6f3', color: '#2a2a4a', fontWeight: 600, fontSize: 12, textAlign: 'center', fontVariantNumeric: 'tabular-nums', minWidth: 28, maxWidth: 32, width: 30 }}>
                    {rankMap[gene] && rankMap[gene][group] ? rankMap[gene][group] : <span style={{ color: '#bbb' }}>-</span>}
                  </td>
                ])}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {toast && <CopyToast message={toast} />}
      <div style={{ marginTop: 16, textAlign: 'center', color: '#666', fontSize: 13, lineHeight: 1.5, background: '#f6f8fa', borderRadius: 7, padding: '10px 18px', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }}>
        <b>Column meanings:</b> <br />
        <b>∑ (Overlap):</b> Number of groups in which the gene appears in the top 50 for that group.<br />
        <b>Value:</b> Expression value for the gene in the given group.<br />
        <b>Rank:</b> Position of the gene within the top 50 for the group (1 = highest value).
      </div>
    </>
  );
};

export default TopGenesTable;
